// Modified from https://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { alertController } from '@ionic/core';
import { AttachmentRaw, Constants, DateUtils, FileDoc, FileType, PhotoAnalysis, PhotoDoc, ProjectDoc } from 'common';
import { authInterceptor } from './AuthService';
import { User } from '../models';
import { ConcourseDb } from '../ConcourseDb';

interface Base64Image {
  src: string;
  width: number;
  height: number;
}

/**
 * Convert a base64 string to a Blob
 *
 * @param {string} base64Data - the base64 encoded string
 * @param {string} contentType - the MIME type of the data
 * @returns a Blob object representing the data
 */
export class ImageService {
  static filterPhotos(files: PhotoDoc[], searchTerm: string): PhotoDoc[] {
    if (!searchTerm || searchTerm.length < 0) return files; //.sort(this.sortByDate);
    const lowerSearchTerm = searchTerm.toLowerCase();
    return files.filter(file => this.filterPhoto(file, lowerSearchTerm)); //.sort(this.sortByDate);
  }

  static filterPhoto(file: PhotoDoc, searchTermLower: string): boolean {
    if (searchTermLower.startsWith('#')) return file.tags?.some(tag => tag.name.toLowerCase().includes(searchTermLower.substring(1)));
    return file.name?.toLowerCase().includes(searchTermLower) || file.description?.toLowerCase().includes(searchTermLower);
  }

  static isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  static base64toBlob(base64Data: string, contentType: string): Blob {
    contentType = contentType || '';
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const byteLength = byteCharacters.length;
    const slicesCount = Math.ceil(byteLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, byteLength);

      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  /**
   * Converts a Blob object to a Base64 string.
   *
   * @param {Blob} blob - The Blob object to be converted.
   * @return {Promise<string>} - A Promise that resolves with the Base64 string.
   */
  static blobToBase64(blob: Blob): Promise<string> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = event => {
        //Remove the prefix from the base64 string
        const str = event.target.result as string;
        const base64 = str.split(',')[1];
        resolve(base64);
      };
    });
  }

  static async blobToBase64ImageUrl(contentType: string, blob: Blob): Promise<string> {
    return `data:${contentType};base64,${await this.blobToBase64(blob)}`;
  }

  static resizeBlob(blob: Blob, maxWidth: number, maxHeight: number, content_type: string = 'image/webp', resize_quality: number = 0.85): Promise<Base64Image> {
    return this.resizeImage(URL.createObjectURL(blob), maxWidth, maxHeight, content_type, resize_quality);
  }

  static resizeBase64(src: string, maxWidth: number, maxHeight: number, content_type: string = 'image/webp', resize_quality: number = 0.85): Promise<Base64Image> {
    return this.resizeImage(src, maxWidth, maxHeight, content_type, resize_quality);
  }

  /**
   * Resizes an image using the given width and height dimensions.
   *
   * @param {Blob} blob - The image file in Blob format.
   * @param {number} width - The desired width of the resized image.
   * @param {number} height - The desired height of the resized image.
   * @param {string} content_type - The MIME type of the resized image. Default is 'image/webp'.
   * @param {number} resize_quality - The quality of the resized image. Default is 0.85.
   * @return {Promise<Blob>} - A Promise that resolves with the resized image in Blob format.
   */
  static resizeImage(src: string, maxWidth: number, maxHeight: number, content_type: string = 'image/webp', resize_quality: number = 0.85): Promise<Base64Image> {
    return new Promise(resolve => {
      const img = document.createElement('img');
      img.src = src;
      img.onload = () => {
        console.log('Image loaded', img.width, img.height);
        let width = img.width;
        let height = img.height;

        if (width >= height && width > maxWidth) {
          // image is landscape, lets make height the max height.
          width = Math.round(width * (maxHeight / height));
          height = maxHeight;
        } else if (height > maxHeight) {
          // image is portrait, lets make width the max width.
          height = Math.round(height * (maxWidth / width));
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, (width / img.width) * img.height); // don't distort image
        canvas.toBlob(
          blob => {
            resolve({ src: canvas.toDataURL('image/jpeg'), width, height });
          },
          content_type,
          resize_quality,
        );
      };
    });
  }

  // resize base64 old
  /*return new Promise(resolve => {
      const img = document.createElement('img');
      img.src = src;
      img.onload = () => {
        console.log('Image loaded', img.width, img.height);
        let width = img.width;
        let height = img.height;

        if (width >= height && width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        } else if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, (width / img.width) * img.height); // don't distort image
        resolve({ src: canvas.toDataURL('image/jpeg'), width, height });
      };
    });*/

  static readFile(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      // read the file choosen by user
      var reader = new FileReader();
      reader.onload = async evt => {
        // upload file to S3 using the presigned url
        resolve(new Blob([evt.target.result as ArrayBuffer]));
      };
      reader.onerror = e => {
        reject(reader.error.message);
      };
      reader.onloadend = function (e) {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  static async takePhoto(): Promise<Photo> {
    console.log('ImageService.takePhoto()');
    const { camera } = await Camera.checkPermissions();
    if (!camera) {
      const { camera: granted } = await Camera.requestPermissions();
      if (!granted) {
        throw new Error('Camera permission denied');
      }
    }
    console.log('Camera.getPhoto()');
    return Camera.getPhoto({
      resultType: CameraResultType.Uri,
      source: CameraSource.Camera,
      quality: 100,
      width: 1000,
    });
  }

  static async takePhotoToTextarea(): Promise<void> {
    console.log('takePhoto: ');
    try {
      const photo = await ImageService.takePhoto();
      console.log('photo: ', photo);
      const commentInput = document.querySelector('concourse-comment') as HTMLConcourseCommentElement;
      if (commentInput) await commentInput.uploadPhoto(photo);
    } catch (e) {
      console.error('Error taking photo: ', e);
      const alert = await alertController.create({
        header: 'Error',
        message: 'Error taking photo. Please try again.',
        buttons: ['OK'],
      });
      await alert.present();
    }
  }

  static async blobToPhotoDoc(fileName: string, blob: Blob, user: User): Promise<PhotoDoc> {
    console.log(`ImageService.blobToPhotoDoc(file: ${fileName}, blob: ${blob}, user: ${user})`);
    const thumb = await this.resizeBlob(blob, Constants.PHOTO_SIZE, Constants.PHOTO_SIZE);
    return this.createPhotoDoc(fileName, thumb, user, blob.size);
  }
  /*
  static async photoToFileDoc(photo: Photo, user: User): Promise<PhotoDoc> {
    console.log(`photoToFileDoc`, photo, user);
    const thumb: Base64Image = await this.resizeBase64(photo.webPath, Constants.PHOTO_SIZE, Constants.PHOTO_SIZE);
    const filename = `photo-${new Date().toISOString()}.${photo.format}`;
    return this.createPhotoDoc(filename, thumb, user, photo.num_bytes);
  }
*/
  static createPhotoDoc(name: string, thumb: Base64Image, user: User, num_bytes: number): PhotoDoc {
    console.log(`ImageService.createPhotoDoc(fileName: ${name}, thumb: ${thumb}, user: ${user})`);
    return {
      _id: ConcourseDb.makeShortDocId(),
      parent_id: null,
      type: FileType.PHOTO,
      name: name,
      thumb: thumb.src.substring(thumb.src.indexOf(',') + 1), // remove the data:image/jpeg;base64,
      thumb_width: thumb.width,
      thumb_height: thumb.height,
      num_bytes: num_bytes,
      created_by: user.id,
      created_date: new Date().toISOString(),
      updated_date: new Date().toISOString(),
    };
  }

  static async uploadBlob(projectId: string, fileId: string, filename: string, blob: Blob, callback: (progress: number) => void): Promise<string> {
    console.log(`ImageService.uploadBlob(projectId: ${projectId}, fileId: ${fileId}, filename: ${filename})`);
    const apiUrl = globalThis.ccLambdaBaseUrl + `/fn/files/getSignedUploadUrl?projectId=${projectId}&fileId=${fileId}&fileName=${encodeURIComponent(filename)}`;
    const response = await authInterceptor.GET(apiUrl);
    const json = await response.json();
    if (response.status != 200) {
      throw new Error(json.message);
    }
    await ImageService.uploadBlobToSignedUrl(json.url, blob, callback);
    return json.key;
  }

  static uploadBlobToSignedUrl(url: string, blob: Blob, callback: (progress: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      var xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (evt: ProgressEvent) => {
        callback(evt.loaded / evt.total);
      });

      xhr.addEventListener('load', () => {
        if (xhr.status != 200) {
          reject(`${xhr.statusText}`);
          return;
        }
        resolve();
      });

      xhr.addEventListener('error', (e: ErrorEvent) => {
        reject(`${e.error}`);
      });
      xhr.open('PUT', url, true);
      xhr.send(blob);
    });
  }

  static async analyzePhoto(projectId: string, photo: PhotoDoc): Promise<PhotoAnalysis> {
    const photoAnalysisUrl = `${globalThis.ccLambdaBaseUrl}/fn/files/photoAnalysis`;
    const response = await authInterceptor.POST(photoAnalysisUrl, { projectId, photo });
    return await response.json();
  }

  static getBase64(attachment: AttachmentRaw): string {
    const contentType = attachment.content_type;
    const blob = this.base64toBlob(attachment.data, contentType);
    return URL.createObjectURL(blob);
  }

  static getThumbnail(doc: ProjectDoc): string {
    if (!doc.thumbnail?.data) {
      return '/assets/img/card-media.png';
    }
    if (doc.thumbnail.data.startsWith('data:')) return doc.thumbnail.data;
    return `data:${doc.thumbnail.mimeType || 'image/jpeg'};base64,${doc.thumbnail.data}`;
  }

  static setThumbnail(doc: ProjectDoc, base64OrSrc: string, user: User): ProjectDoc {
    const src = base64OrSrc.includes(',') ? base64OrSrc : `data:image/jpeg;base64,${base64OrSrc}`;
    const comma = src.indexOf(',');
    const meta = comma >= 0 ? src.substring(0, comma) : '';
    const mimeMatch = meta.match(/data:([^;]+)/);
    return {
      ...doc,
      thumbnail: {
        data: comma >= 0 ? src.substring(comma + 1) : src,
        mimeType: mimeMatch?.[1] || 'image/jpeg',
        width: 0,
        height: 0,
      },
      updated_date: new Date().toISOString(),
      updated_by: user.id,
    };
  }

  static photosByMonth(photos: PhotoDoc[], addThisMonth: boolean): { date: Date; photos: PhotoDoc[] }[] {
    const months: { date: Date; photos: PhotoDoc[] }[] = [];
    if (photos.length > 0) {
      const minDate: Date = DateUtils.fromISO(photos.reduce((min, p) => (p.created_date < min ? p.created_date : min), photos[0].created_date));
      const maxDate: Date = DateUtils.fromISO(photos.reduce((max, p) => (p.created_date > max ? p.created_date : max), photos[0].created_date));
      minDate.setDate(1);
      minDate.setHours(0, 0, 0, 0);

      for (var i: Date = minDate; i <= maxDate; i.setMonth(i.getMonth() + 1)) {
        const monthPhotos = photos.filter(p => this.isSameMonth(i, DateUtils.fromISO(p.created_date)) && !p.deleted_by);
        if (monthPhotos.length > 0) {
          months.push({ date: new Date(i), photos: monthPhotos });
        }
      }
    }

    const today = new Date();
    // If this month is not included, add it here..
    if (addThisMonth && !months.find(m => this.isSameMonth(m.date, today))) {
      months.push({ date: today, photos: [] });
    }
    return months;
  }

  static isSameMonth = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
  };

  static photosByDay(photos: PhotoDoc[], addToday: boolean): { date: Date; photos: PhotoDoc[] }[] {
    const days: { date: Date; photos: PhotoDoc[] }[] = [];
    if (photos.length > 0) {
      const minDate: Date = DateUtils.fromISO(photos.reduce((min, p) => (p.created_date < min ? p.created_date : min), photos[0].created_date));
      const maxDate: Date = DateUtils.fromISO(photos.reduce((max, p) => (p.created_date > max ? p.created_date : max), photos[0].created_date));
      minDate.setHours(0, 0, 0, 0);

      for (var i: Date = minDate; i <= maxDate; i.setDate(i.getDate() + 1)) {
        const dayPhotos = photos.filter(p => this.isSameDay(i, DateUtils.fromISO(p.created_date)));
        if (dayPhotos.length > 0) {
          days.push({ date: new Date(i), photos: dayPhotos });
        }
      }
    }

    const today = new Date();
    // If this month is not included, add it here..
    if (addToday && !days.find(m => this.isSameDay(m.date, today))) {
      days.push({ date: today, photos: [] });
    }
    return days;
  }

  static isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
  };
}
